/*
 * IMPLEMENTACIÓN ÚNICA DEL FORMATO Y LA VALIDACIÓN DE IMPORTES
 * (RF-086, D-82, D-87, D-88, D-89, D-90; RN-135 a RN-139, RN-146 a RN-149).
 *
 * SE ESCRIBE UNA VEZ Y TODOS LOS CAMPOS MONETARIOS LA USAN. Si una pantalla vuelve
 * a formatear por su cuenta, es un defecto de construcción (RN-147d): dos formatos
 * conviven mal y uno de los dos se queda viejo, que es exactamente lo que pasó con
 * el prefijo derogado del dólar.
 *
 * ES PRESENTACIÓN Y VALIDACIÓN DE CAPTURA, NADA MÁS (RN-137, RN-146g):
 *   * El valor que viaja y se guarda sigue siendo CADENA DECIMAL de dos decimales
 *     (RNF-001, RNF-002).
 *   * NO se introduce ningún redondeo: la aritmética en centavos enteros (RNF-033)
 *     y el residuo en la última mensualidad (RN-100, D-55) no se tocan. Este
 *     archivo recibe un importe ya calculado y sólo lo presenta; los decimales
 *     sobrantes se TRUNCAN al mostrar, nunca se redondean.
 *   * NO crea código de error de API ni cambia ningún contrato (RN-148f).
 */

/** RN-146d: los dos códigos que existen. No hay conversión entre ellos. */
export const DIVISAS = Object.freeze({ MXN: 'MXN', USD: 'USD' });

/** Lo que se muestra cuando no hay importe que mostrar. */
export const SIN_IMPORTE = '—';

/** Resultados de interpretar lo que la persona teclea o pega. */
export const ENTRADA = Object.freeze({
  VACIA: 'VACIA',
  VALIDA: 'VALIDA',
  DIVISA_CRUZADA: 'DIVISA_CRUZADA',
  NEGATIVA: 'NEGATIVA',
  DEMASIADOS_DECIMALES: 'DEMASIADOS_DECIMALES',
  NO_NUMERICA: 'NO_NUMERICA',
});

/**
 * RN-146 · FORMATO DE IMPORTE, CARÁCTER POR CARÁCTER:
 *   [signo] $ <entero con separador de miles> . <dos decimales> <espacio> <CÓDIGO>
 *
 *   (a) `$` pegado al primer dígito, PARA MXN Y PARA USD POR IGUAL. El prefijo
 *       propio del dólar quedó DEROGADO y no se escribe en ninguna pantalla
 *       (RN-147b).
 *   (b) coma cada tres dígitos de la parte entera.
 *   (c) punto decimal, SIEMPRE con exactamente dos decimales, aunque sean ceros.
 *   (d) código en mayúsculas al final, precedido de UN SOLO espacio.
 *   (e) el signo menos va ANTES del símbolo, sin espacio: `-$1,234.56 MXN`.
 *   (g) formatear y volver a leer devuelve el mismo importe, sin perder un centavo.
 *
 * Ejemplos normativos: `$0.00 MXN` · `$5.00 MXN` · `$1,234.56 MXN` ·
 * `$1,299.00 USD` · `-$1,234.56 MXN` · `$1,000,000.00 MXN` · `$14.32 MXN`.
 */
export function formatearImporte(valor, codigoDivisa) {
  if (valor === null || valor === undefined || valor === '') return SIN_IMPORTE;

  const partes = separarImporte(valor);
  // Un texto que no es un importe no se formatea ni se disfraza: se devuelve tal
  // cual para que se vea que algo va mal (RN-139c).
  if (!partes) return String(valor);

  const entero = agruparMiles(partes.entero);
  const cuerpo = `$${entero}.${partes.decimales}`;

  // RN-149d: el cero negativo NO existe en presentación. `-0.00` y `-0.001` se
  // muestran `$0.00`, nunca con signo.
  const esCero = /^0+$/.test(partes.entero) && /^0+$/.test(partes.decimales);
  const signo = partes.negativo && !esCero ? '-' : '';

  const codigo = normalizarCodigo(codigoDivisa);
  return codigo ? `${signo}${cuerpo} ${codigo}` : `${signo}${cuerpo}`;
}

/**
 * RN-146h / RN-137: en modo edición el campo muestra el número PELADO —sin
 * símbolo, sin separador de miles y sin código— para que se pueda escribir encima.
 */
export function valorPelado(valor) {
  if (valor === null || valor === undefined) return '';
  const partes = separarImporte(valor);
  if (!partes) return String(valor);
  return `${partes.negativo ? '-' : ''}${partes.entero}.${partes.decimales}`;
}

/**
 * Normaliza a la cadena decimal de dos decimales que viaja al servidor. NO
 * redondea: trunca lo que sobre de dos decimales, porque el importe que llega ya
 * viene calculado en centavos enteros (RNF-033).
 */
export function normalizarImporte(valor) {
  const partes = separarImporte(valor);
  if (!partes) return null;
  const esCero = /^0+$/.test(partes.entero) && /^0+$/.test(partes.decimales);
  return `${partes.negativo && !esCero ? '-' : ''}${partes.entero}.${partes.decimales}`;
}

/**
 * INTERPRETA LO QUE LA PERSONA TECLEA O PEGA, en la divisa del contexto.
 *
 * RN-139a / RN-148: un importe con separadores o símbolo y SIN código, o con el
 * código de la divisa del propio contexto, se acepta y se normaliza. Con un código
 * DISTINTO se RECHAZA (D-89): tomar `$100.00 USD` como `100.00` en una cuenta MXN
 * sería una conversión implícita a tipo 1:1, y las monedas están separadas y sin
 * conversión.
 *
 * RN-148d: el prefijo derogado del dólar cuenta como código `USD` para esta
 * validación, aunque ya no sea formato válido de salida.
 * RN-149: el signo menos no se captura, ni tecleado ni pegado.
 * RN-139b: más de dos decimales se rechaza SIN recortar en silencio.
 * RN-139c: un texto no numérico no se formatea, se marca inválido.
 */
export function interpretarImporte(texto, codigoDivisa) {
  const contexto = normalizarCodigo(codigoDivisa);
  const crudo = String(texto ?? '').trim();

  // RN-136: un campo vacío no inventa un valor; se queda vacío y actúa la
  // validación obligatoria donde ya esté definida (por ejemplo RN-018).
  if (crudo === '') return { estado: ENTRADA.VACIA, valor: '', divisaDetectada: null };

  const divisaDetectada = detectarDivisa(crudo);
  if (divisaDetectada && contexto && divisaDetectada !== contexto) {
    return { estado: ENTRADA.DIVISA_CRUZADA, valor: null, divisaDetectada };
  }

  const sinAdornos = crudo
    .replace(/US\$/gi, '')
    .replace(/\b(MXN|USD)\b/gi, '')
    .replace(/\$/g, '')
    .replace(/\s/g, '')
    .replace(/,/g, '');

  if (sinAdornos === '') return { estado: ENTRADA.NO_NUMERICA, valor: null, divisaDetectada };

  // RN-149a: ningún campo editable admite el signo menos. Se detecta antes que
  // cualquier otra cosa para que el mensaje sea el correcto.
  if (/^[-−]/.test(sinAdornos)) {
    return { estado: ENTRADA.NEGATIVA, valor: null, divisaDetectada };
  }

  if (/^\d+\.\d{3,}$/.test(sinAdornos)) {
    return { estado: ENTRADA.DEMASIADOS_DECIMALES, valor: null, divisaDetectada };
  }

  if (!/^\d+(\.\d{1,2})?$/.test(sinAdornos)) {
    return { estado: ENTRADA.NO_NUMERICA, valor: null, divisaDetectada };
  }

  return { estado: ENTRADA.VALIDA, valor: normalizarImporte(sinAdornos), divisaDetectada };
}

/**
 * Mensaje de cada rechazo. Nombra LAS DOS DIVISAS en el cruzado (RN-148b) y
 * recuerda que el negativo sólo existe como resultado (RN-149c).
 *
 * `contexto` es cómo se llama el ámbito en la frase: "esta cuenta", "este plan".
 */
export function mensajeDeEntrada(resultado, codigoDivisa, contexto = 'este campo') {
  const divisa = normalizarCodigo(codigoDivisa) ?? '';
  switch (resultado.estado) {
    case ENTRADA.DIVISA_CRUZADA:
      return `El importe pegado está en ${resultado.divisaDetectada} y ${contexto} opera en ${divisa}. `
        + `No se convierte entre monedas: teclea el importe en ${divisa}.`;
    case ENTRADA.NEGATIVA:
      return 'Un importe capturado no puede ser negativo. El signo menos sólo aparece en importes '
        + 'calculados, como una deuda a tu favor.';
    case ENTRADA.DEMASIADOS_DECIMALES:
      return 'Un importe lleva como mucho dos decimales. No se recorta solo: corrígelo.';
    case ENTRADA.NO_NUMERICA:
      return 'Eso no es un importe. Escribe sólo cifras, con punto decimal si hace falta.';
    default:
      return null;
  }
}

/** Verdadero si el texto trae un código de divisa distinto al del contexto. */
export function esDivisaCruzada(texto, codigoDivisa) {
  return interpretarImporte(texto, codigoDivisa).estado === ENTRADA.DIVISA_CRUZADA;
}

/**
 * Divisa declarada EXPLÍCITAMENTE en el texto, o null si no trae ninguna
 * (RN-148e: sin código no hay divisa cruzada que detectar).
 */
function detectarDivisa(texto) {
  if (/US\$/i.test(texto)) return DIVISAS.USD; // prefijo derogado (RN-148d)
  const codigo = /\b(MXN|USD)\b/i.exec(texto);
  return codigo ? codigo[1].toUpperCase() : null;
}

/**
 * Parte un importe en signo, entero y exactamente dos decimales. Trabaja sobre el
 * TEXTO, no sobre un número de coma flotante: así un millón de pesos y un centavo
 * se presentan exactos (RN-146g).
 */
function separarImporte(valor) {
  const texto = String(valor).trim().replace(/,/g, '').replace(/\s/g, '');
  const partido = /^([-−+])?(\d*)(?:\.(\d*))?$/.exec(texto);
  if (!partido) return null;

  const entero = partido[2] === '' || partido[2] === undefined ? '0' : partido[2];
  if (partido[2] === '' && (partido[3] === undefined || partido[3] === '')) return null;

  return {
    negativo: partido[1] === '-' || partido[1] === '−',
    entero: entero.replace(/^0+(?=\d)/, ''),
    // Dos decimales exactos: se rellena con ceros y se TRUNCA lo que sobre.
    decimales: `${partido[3] ?? ''}00`.slice(0, 2),
  };
}

function agruparMiles(entero) {
  return entero.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function normalizarCodigo(codigo) {
  if (typeof codigo !== 'string' || codigo.trim() === '') return null;
  return codigo.trim().toUpperCase();
}
